/**
 * Post-Hearing Update Modal
 *
 * After a hearing ends, the clerk enters what happened:
 * - Outcome (won/lost/adjourned/settled/directions)
 * - Orders made by the judge
 * - Next hearing date
 * - Deadlines (skeleton due dates, filing dates)
 * - Costs position
 * - Counsel's verbal notes
 *
 * This updates: hearing record, matter status on pipeline,
 * diary (next hearing), tasks (deadlines), and notifies the barrister.
 */
import { useState } from 'react';
import {
  X,
  Gavel,
  CheckCircle,
  ListChecks,
} from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { toast } from 'sonner';
import { type Hearing, type HearingOutcome } from '@/lib/hearingsStore';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  open: boolean;
  hearing: Hearing | null;
  clerkName: string;
  onClose: () => void;
  onSave: (update: {
    hearingId: string;
    result: HearingOutcome;
    /** Canonical backend outcomeType — drives post-hearing follow-up tasks + reports. */
    outcomeType: string;
    /** True when the court reserved judgment — a reserved judgment must never be lost. */
    judgmentReserved: boolean;
    /** True when a costs order was made (anything other than "no order"). */
    costsOrdered: boolean;
    outcome: string;
    orders: string;
    counselNotes: string;
    nextHearingDate: string;
    costsPosition: string;
    deadlines: string;
    /** Return (or resolve) false when the save was refused — the modal then stays open with the entry intact. */
  }) => void | boolean | Promise<void | boolean>;
}

const OUTCOMES: { value: HearingOutcome; label: string; icon: string; color: string }[] = [
  { value: 'won',        label: 'Won / Granted',     icon: '✅', color: 'border-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-300' },
  { value: 'lost',       label: 'Lost / Dismissed',  icon: '❌', color: 'border-red-400 bg-red-50 dark:bg-red-500/10 text-red-800 dark:text-red-300' },
  { value: 'adjourned',  label: 'Adjourned',         icon: '⏸️', color: 'border-amber-400 bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300' },
  { value: 'reserved',   label: 'Judgment reserved', icon: '⚖️', color: 'border-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-800 dark:text-indigo-300' },
  { value: 'settled',    label: 'Settled',            icon: '🤝', color: 'border-blue-400 bg-blue-50 dark:bg-blue-500/10 text-blue-800 dark:text-blue-300' },
  { value: 'directions', label: 'Directions Given',  icon: '📋', color: 'border-brand-400 bg-brand-50 text-brand-800 dark:text-brand-200' },
  { value: 'part_heard', label: 'Part Heard',         icon: '⏳', color: 'border-violet-400 bg-violet-50 dark:bg-violet-500/10 text-violet-800 dark:text-violet-300' },
  { value: 'withdrawn',  label: 'Withdrawn',          icon: '↩️', color: 'border-slate-400 bg-slate-50 dark:bg-slate-800/40 text-slate-700 dark:text-slate-300' },
];

// Map the clerk's plain-language result to the canonical backend outcomeType so
// the API fires the right follow-up tasks (advise client, chase judgment, monitor
// compliance, schedule next hearing) and reports classify the hearing correctly.
const OUTCOME_TYPE_MAP: Record<HearingOutcome, string> = {
  won: 'final_judgment',
  lost: 'final_judgment',
  adjourned: 'adjourned',
  reserved: 'judgment_reserved',
  settled: 'settlement_reached',
  directions: 'directions_given',
  part_heard: 'trial_continued',
  dismissed: 'strike_out',
  withdrawn: 'matter_withdrawn',
  '': '',
};

const labelCls = 'mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground';
const inputCls = 'h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100';

export function PostHearingModal({ open, hearing, clerkName, onClose, onSave }: Props) {
  const [result, setResult] = useState<HearingOutcome>('');
  const [outcomeNotes, setOutcomeNotes] = useState('');
  const [orders, setOrders] = useState('');
  const [counselNotes, setCounselNotes] = useState('');
  const [nextDate, setNextDate] = useState('');
  const [costs, setCosts] = useState('');
  const [deadlines, setDeadlines] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open || !hearing) return null;

  const NEEDS_NEXT_DATE: HearingOutcome[] = ['adjourned', 'part_heard', 'directions'];
  const notesOk = outcomeNotes.trim().length >= 10;
  const nextDateOk = !NEEDS_NEXT_DATE.includes(result) || nextDate !== '';
  const valid = result !== '' && notesOk && nextDateOk;
  // Which outcomes actually move the matter (conservative, matches the backend). Presentation
  // only — the backend owns outcome → status (matterStatusForOutcome); the UI never decides it.
  const MATTER_MOVE_LABELS: Partial<Record<HearingOutcome, string>> = {
    settled: 'Settled',
    withdrawn: 'Withdrawn',
    reserved: 'Judgment reserved',
  };
  const matterMoveLabel = MATTER_MOVE_LABELS[result];

  const handleSave = async () => {
    if (submitting) return;
    setSubmitting(true);
    // Wait for the save: this used to announce "Hearing updated" and close before the server had
    // answered, so a refused outcome looked recorded and the clerk's entry was lost.
    const saved = await onSave({
      hearingId: hearing.id,
      result,
      outcomeType: OUTCOME_TYPE_MAP[result] ?? '',
      judgmentReserved: result === 'reserved',
      costsOrdered: costs.trim() !== '' && costs.trim() !== 'No order as to costs',
      outcome: outcomeNotes.trim(),
      orders: orders.trim(),
      counselNotes: counselNotes.trim(),
      nextHearingDate: nextDate,
      costsPosition: costs.trim(),
      deadlines: deadlines.trim(),
    });
    if (saved === false) { setSubmitting(false); return; }
    toast.success(`Hearing updated — ${hearing.matterName} · ${OUTCOMES.find((o) => o.value === result)?.label ?? result}`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4">
      <ScrollLock />
      <div className="flex min-h-full items-center justify-center">
      <Card className="w-full max-w-lg overflow-hidden shadow-pop">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border bg-brand-50 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-card">
              <Gavel className="h-5 w-5 text-brand-600 dark:text-brand-400" />
            </div>
            <div>
              <div className="text-base font-semibold text-foreground">Post-Hearing Update</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {hearing.matterName} · {hearing.type} · {hearing.court}
              </div>
            </div>
          </div>
          <button className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <CardContent className="max-h-[70vh] space-y-4 overflow-y-auto p-6">
          {/* Hearing info summary */}
          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs">
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-muted-foreground">Barrister:</span> <span className="font-medium text-foreground">{hearing.leadBarrister}</span></div>
              <div><span className="text-muted-foreground">Date:</span> <span className="font-medium text-foreground">{hearing.date}</span></div>
              <div><span className="text-muted-foreground">Court:</span> <span className="font-medium text-foreground">{hearing.court}</span></div>
              <div><span className="text-muted-foreground">Judge:</span> <span className="font-medium text-foreground">{hearing.judge || '—'}</span></div>
            </div>
          </div>

          {/* Outcome selection */}
          <div>
            <label className={labelCls}>What happened? *</label>
            <div className="grid grid-cols-2 gap-2">
              {OUTCOMES.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => setResult(o.value)}
                  className={`rounded-md border p-2.5 text-left transition ${
                    result === o.value ? o.color + ' ring-1' : 'border-border hover:border-brand-200 hover:bg-muted/40'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span>{o.icon}</span>
                    <span className="text-xs font-semibold">{o.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Matter-status consequence — conservative: only a clean disposal or a
              reserved judgment moves the matter; a won/lost judgment stays open
              until post-hearing admin is done and the clerk closes it. */}
          {result && (
            matterMoveLabel ? (
              <div className="flex items-center gap-2 rounded-md border border-brand-200 bg-brand-50 p-2.5 text-xs text-brand-900 dark:text-brand-200">
                <ListChecks className="h-3.5 w-3.5 shrink-0" />
                Matter will move to <Badge>{matterMoveLabel}</Badge>
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 p-2.5 text-xs text-muted-foreground">
                <ListChecks className="h-3.5 w-3.5 shrink-0" />
                Matter stays open — complete the post-hearing admin (fee note, sealed order) before closing.
              </div>
            )
          )}

          {/* Outcome notes — required */}
          <div>
            <label className={labelCls}>
              Outcome summary <span className="text-red-500">*</span>
            </label>
            <ExpandableTextarea
              label="Outcome summary"
              value={outcomeNotes}
              onChange={setOutcomeNotes}
              rows={3}
              placeholder="e.g. Judge granted application for summary judgment. Defence had no real prospect of success."
            />
            {result && !notesOk && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                Brief summary required — at least a sentence about what happened in court.
              </p>
            )}
          </div>

          {/* Orders */}
          <div>
            <label className={labelCls}>Orders made by the court</label>
            <ExpandableTextarea
              label="Orders made by the court"
              value={orders}
              onChange={setOrders}
              rows={2}
              placeholder="e.g. Defence statement to be served by 28 April. Trial estimate: 5 sitting days."
            />
          </div>

          {/* Next hearing + deadlines */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>
                Next hearing date
                {NEEDS_NEXT_DATE.includes(result) && (
                  <span className="text-red-500"> *</span>
                )}
              </label>
              <input
                type="date"
                value={nextDate}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setNextDate(e.target.value)}
                className={`${inputCls} ${NEEDS_NEXT_DATE.includes(result) && !nextDate ? 'border-red-300 dark:border-red-500/40' : ''}`}
              />
              {NEEDS_NEXT_DATE.includes(result) && !nextDate && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">Required for {result.replace('_', ' ')} outcomes.</p>
              )}
            </div>
            <div>
              <label className={labelCls}>Costs position</label>
              <select value={costs} onChange={(e) => setCosts(e.target.value)} className={inputCls}>
                <option value="">— Select —</option>
                <option value="Costs awarded to client">Costs to client</option>
                <option value="Costs awarded to other side">Costs to other side</option>
                <option value="Costs reserved">Costs reserved</option>
                <option value="Costs in the case">Costs in the case</option>
                <option value="No order as to costs">No order as to costs</option>
                <option value="Summary assessment">Summary assessment</option>
                <option value="Detailed assessment">Detailed assessment</option>
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>Key deadlines set by the court</label>
            <ExpandableTextarea
              label="Key deadlines set by the court"
              value={deadlines}
              onChange={setDeadlines}
              rows={2}
              placeholder="e.g. Skeleton argument due 5 May. Bundle to be filed by 8 May."
            />
          </div>

          {/* Counsel notes */}
          <div>
            <label className={labelCls}>Counsel{'\u2019'}s verbal notes</label>
            <ExpandableTextarea
              label="Counsel's verbal notes"
              value={counselNotes}
              onChange={setCounselNotes}
              rows={2}
              placeholder="What did the barrister say about how it went? Any concerns for next hearing?"
            />
          </div>

          <div className="rounded-md border border-blue-100 bg-blue-50 dark:bg-blue-500/10 p-2.5 text-xs text-blue-900 dark:text-blue-200">
            <strong>What happens when you save:</strong> Hearing marked as completed. Matter moves on the pipeline.
            {nextDate && ' Next hearing added to diary.'}
            {deadlines && ' Tasks created for deadlines.'}
            {' '}Barrister notified. Instructing solicitor receives a hearing update in their portal.
          </div>
        </CardContent>

        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <div className="text-xs text-muted-foreground">Entered by {clerkName}</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="md" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="md" leftIcon={<CheckCircle className="h-4 w-4" />} disabled={!valid || submitting} onClick={handleSave}>
              {submitting ? 'Saving…' : 'Save update'}
            </Button>
          </div>
        </div>
      </Card>
      </div>
    </div>
  );
}
